// PaymentSummaryPreview.js — PAYMENT-SUMMARY-REPORT-1 (2026-05-19)
//
// Preview surface for the per-claim Payment Summary / Payment History report.
// Models the WNW Payment History Report reference template (Banther).
//
// Reuses the same action-bar + overflow + print-stylesheet pattern as
// ClaimSummaryPreview.  The data payload (data.rows / category_totals /
// funding_totals / accounting_summary / pending_ir_items) feeds three
// surfaces identically: this in-browser preview, the ReportLab landscape
// PDF, and the openpyxl landscape XLSX.

import { Fragment, useEffect, useRef, useState } from "react";
import { apiFetch, fmt } from "../api";
import ComposeEmailModal from "./ComposeEmailModal";

const PRINT_STYLE = `
@page { size: landscape; margin: 0.4in; }
@media print {
  body * { visibility: hidden !important; }
  .ps-preview-print-target,
  .ps-preview-print-target * { visibility: visible !important; }
  .ps-preview-print-target {
    position: absolute !important;
    left: 0 !important; top: 0 !important;
    width: 100% !important;
    box-shadow: none !important;
    border: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .ps-preview-print-target * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .ps-preview-actions { display: none !important; }
}
`;

const FALLBACK_NAVY = "#1F3864";
const FALLBACK_ACCENT = "#2E75B6";
const FALLBACK_HEADER_TEXT = "#FFFFFF";
const BG_ALT = "#F8F9FB";
const BORDER_GRAY = "#D0D7E0";
const TEXT_MUTED = "#777";
const TEXT_BODY = "#1A1A1A";

function resolveTheme(branding) {
  const b = branding || {};
  const primary = b.primary_color || FALLBACK_NAVY;
  const secondary = b.secondary_color || FALLBACK_ACCENT;
  return {
    primary,
    secondary,
    headerBg:     b.table_header_bg_color || primary,
    headerText:   b.table_header_text_color || FALLBACK_HEADER_TEXT,
    sectionColor: b.section_heading_color || primary,
    footerAccent: b.footer_accent_color || secondary,
  };
}

export default function PaymentSummaryPreview({ claimId, canEdit = false, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [flash, setFlash] = useState(null);
  const [compose, setCompose] = useState(null);
  async function fileReport() {
    return apiFetch(`/claims/${claimId}/reports/payment-summary/to-documents`, { method: "POST", body: {} });
  }
  async function saveToDocuments() {
    if (busy) return; setBusy(true); setMenuOpen(false); setFlash(null);
    try { const r = await fileReport(); setFlash({ type: "ok", text: `Saved “${r.filename}” to Documents.` }); }
    catch (e) { setFlash({ type: "err", text: e.message || "Could not save report." }); }
    setBusy(false);
  }
  async function emailReport() {
    if (busy) return; setBusy(true); setMenuOpen(false); setFlash(null);
    try { const r = await fileReport(); const cnum = data && data.claim ? data.claim.claim_number : "";
      setCompose({ document_ids: [r.document_id], subject: `Payment Summary — ${cnum}` }); }
    catch (e) { setFlash({ type: "err", text: e.message || "Could not prepare the email." }); }
    setBusy(false);
  }

  useEffect(() => {
    let alive = true;
    apiFetch(`/claims/${claimId}/reports/payment-summary`)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e.message || String(e)); });
    return () => { alive = false; };
  }, [claimId]);

  useEffect(() => {
    function onClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  async function download(format) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await apiFetch(
        `/claims/${claimId}/reports/payment-summary/export?format=${format}`,
        { raw: true }
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(disp);
      a.download = m ? m[1] : `payment-history.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Download failed: " + (e.message || e));
    } finally {
      setBusy(false);
    }
  }

  function doPrint() { window.print(); }

  if (err) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ color: "#B00020", marginBottom: 12 }}>
          Couldn't load Payment History: {err}
        </div>
        <button className="btn btn-outline btn-sm" onClick={onClose}>← Back to Claim Reports</button>
      </div>
    );
  }
  if (!data) {
    return <div style={{ padding: 20, color: TEXT_MUTED }}>Loading Payment History…</div>;
  }

  const claim = data.claim;
  const branding = data.branding || {};
  const theme = resolveTheme(branding);
  const catKeys   = data.category_keys || [];
  const catLabels = data.category_labels || {};
  const summary   = data.accounting_summary || {};
  const orgName   = branding.org_name || "Walters Northwest Claims Services";

  return (
    <div style={{ padding: 8 }}>
      <style>{PRINT_STYLE}</style>

      {/* Action bar */}
      <div className="ps-preview-actions" style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap",
      }}>
        <button className="btn btn-outline btn-sm" onClick={onClose} title="Back to Claim Reports">
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => download("pdf")}
                title="Download as PDF">⤓ PDF</button>
        <button className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => download("xlsx")}
                title="Download as Excel">⤓ Excel</button>
        <button className="btn btn-outline btn-sm"
                onClick={doPrint}
                title="Print this report">🖨 Print</button>
        {/* H17: "Save to Documents" / "Email Report" file a PDF onto the claim —
            a claim document write, gated server-side on an edit cap. Hidden here
            without one; PDF / Excel / Print are reads and stay available. */}
        {canEdit && <div ref={menuRef} style={{ position: "relative" }}>
          <button className="btn btn-outline btn-sm"
                  onClick={() => setMenuOpen(o => !o)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}>
            More actions ▾
          </button>
          {menuOpen && (
            <div style={{
              position: "absolute", right: 0, top: "calc(100% + 4px)",
              background: "white", border: `1px solid ${BORDER_GRAY}`,
              borderRadius: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
              minWidth: 240, zIndex: 50, padding: 4,
            }} role="menu">
              <button className="btn btn-outline btn-sm" style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 4 }}
                      onClick={saveToDocuments} disabled={busy}>💾 Save to Documents</button>
              <button className="btn btn-outline btn-sm" style={{ display: "block", width: "100%", textAlign: "left" }}
                      onClick={emailReport} disabled={busy}>✉ Email Report</button>
            </div>
          )}
        </div>}
      </div>
      {flash && <div style={{ margin: "8px 0", padding: "7px 12px", borderRadius: 6, fontSize: 13,
        background: flash.type === "ok" ? "#dcfce7" : "#fee2e2", color: flash.type === "ok" ? "#166534" : "#991b1b" }}>{flash.text}</div>}
      {compose && <ComposeEmailModal claimId={claimId} claim={data ? data.claim : null}
        prefill={{ subject: compose.subject, document_ids: compose.document_ids }}
        onClose={() => setCompose(null)} onSent={() => { setCompose(null); setFlash({ type: "ok", text: "Report emailed." }); }} />}

      {/* Printable body — wider than Claim Summary because of the 17-col table */}
      <div className="ps-preview-print-target" style={{
        background: "white",
        border: `1px solid ${BORDER_GRAY}`,
        borderRadius: 4,
        padding: 22,
        maxWidth: 1200,
        margin: "0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        color: TEXT_BODY,
      }}>
        {/* Hero zone — left: title + generated + metadata grid;
            right: letterhead banner.  Sharing one horizontal band so the
            metadata is tucked beside the logo (not stranded below it). */}
        <HeroZone branding={branding} orgName={orgName}
                  primary={theme.primary}
                  footerAccent={theme.footerAccent}
                  generatedBy={data.meta.generated_by}
                  generatedAt={data.meta.generated_at}
                  claim={claim} />

        {/* Main payment table */}
        <PaymentTable rows={data.rows} catKeys={catKeys} catLabels={catLabels} theme={theme} />

        {/* Category totals row */}
        <CategoryTotalsRow catKeys={catKeys}
                           catTotals={data.category_totals}
                           grandTotal={data.grand_total}
                           theme={theme} />

        {/* Party Settlement Ledger (PAYMENT-HISTORY-REDESIGN C3/C4): ONE merged
            table replaces Trust Activity + Settlement — union of funders and
            subscribers, Insured first-class, Funded & Owed co-equal, net position,
            expandable deposit detail, trust cash reconciliation footer. */}
        <PartyLedgerZone ledger={data.party_settlement_ledger}
                         trustActivity={data.trust_activity}
                         settlement={data.settlement_register}
                         theme={theme} />

        {/* Net-due bridge + Indemnity/Expense rollup (Net Due last, D5 order) */}
        <NetDueBridge ledger={data.party_settlement_ledger}
                      kindRollup={data.kind_rollup}
                      settlement={data.settlement_register}
                      summary={summary}
                      pendingIRItems={data.pending_ir_items || []}
                      theme={theme} />

        {/* Confidentiality (centered, end of body) */}
        <ConfidentialityLine branding={branding} />

        {/* Page footer — WNW contact block at bottom of preview */}
        <PageFooter branding={branding} theme={theme} />
      </div>
    </div>
  );
}

function HeroZone({ branding, orgName, primary, footerAccent,
                    generatedBy, generatedAt, claim }) {
  // Single horizontal band: left zone = title + generated + metadata
  // grid; right zone = letterhead banner.  Both zones share the same
  // top alignment so the metadata sits beside the logo, not below it.
  //
  // Auth-gated letterhead: <img src> can't carry the JWT bearer, so
  // we apiFetch the bytes, build an object URL, and render that.
  const [blobUrl, setBlobUrl] = useState(null);
  const [imgErr, setImgErr] = useState(false);
  const url = branding && branding.letterhead_available && branding.letterhead_url;
  useEffect(() => {
    if (!url) { setBlobUrl(null); return; }
    let revoked = null, alive = true;
    apiFetch(url, { raw: true })
      .then(r => r.blob())
      .then(blob => {
        if (!alive) return;
        revoked = URL.createObjectURL(blob);
        setBlobUrl(revoked);
      })
      .catch(() => { if (alive) setImgErr(true); });
    return () => { alive = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [url]);

  const genLine = generatedAt
    ? `Generated by ${generatedBy}  ·  ${generatedAt.replace("T", " ").substring(0, 16)} UTC`
    : `Generated by ${generatedBy}`;

  // UW Ref. — only render the "— ref#" segment when a real reference
  // exists; never invent a placeholder.  Lead UW marked visually.
  const uwList = claim.underwriters || [];
  const uwRefValue = uwList.length === 0 ? "—" : (
    <div>
      {uwList.map((uw, i) => (
        <div key={i} style={{ marginBottom: 1, lineHeight: 1.35 }}>
          <span style={{ fontWeight: uw.is_lead ? 600 : 400 }}>{uw.company_name}</span>
          {uw.participation_pct != null && (
            <span style={{ color: "#777", marginLeft: 4 }}>
              ({uw.participation_pct.toFixed(1)}%)
            </span>
          )}
          {uw.reference_number && (
            <span style={{ color: "#444", marginLeft: 4 }}>
              — {uw.reference_number}
            </span>
          )}
          {uw.is_lead && (
            <span style={{ color: "#777", marginLeft: 4, fontStyle: "italic", fontSize: 9 }}>
              (Lead)
            </span>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        marginBottom: 4,
      }}>
        {/* LEFT zone: title + generated + metadata grid (~66% width) */}
        <div style={{ flex: "1 1 0%", minWidth: 0 }}>
          <div style={{
            fontSize: 24, fontWeight: 700, color: TEXT_BODY,
            lineHeight: 1.15, marginTop: 2,
          }}>
            Payment History Report
          </div>
          <div style={{ fontSize: 10, color: "#555", marginTop: 3, marginBottom: 10 }}>
            {genLine}
          </div>

          {/* Compact metadata grid inside the LEFT zone, two sub-columns */}
          <MetaGrid claim={claim} uwRefValue={uwRefValue} />
        </div>

        {/* RIGHT zone: letterhead banner (~32% width) */}
        <div style={{
          flex: "0 0 32%",
          textAlign: "right",
          alignSelf: "flex-start",
          paddingTop: 2,
        }}>
          {blobUrl && !imgErr ? (
            <img src={blobUrl} alt={orgName}
                 onError={() => setImgErr(true)}
                 style={{ display: "inline-block", width: "100%",
                          maxWidth: "100%", height: "auto" }} />
          ) : (imgErr || !url) ? (
            <div style={{ fontSize: 13, fontWeight: 700, color: primary,
                          textAlign: "right", lineHeight: 1.2 }}>
              {orgName}
            </div>
          ) : (
            <div style={{ height: 72 }} />
          )}
        </div>
      </div>

      {/* Full-width accent rule under the hero zone. */}
      <div style={{ height: 1, background: footerAccent || primary,
                    opacity: 0.55, marginTop: 6, marginBottom: 12 }} />
    </>
  );
}

function MetaGrid({ claim, uwRefValue }) {
  // Two-column metadata grid inside the LEFT zone of the hero.
  // Labels are nowrap (Claim Number must NEVER wrap into Claim/Number).
  // Values may wrap only where naturally long (UW Ref).
  const rows = [
    ["Claim Number:", claim.claim_number || "—",
     "Vessel:",       claim.vessel_name || "—"],
    ["Date of Loss:", fmt.date(claim.date_of_loss),
     "Insured:",      claim.insured_name || "—"],
    ["Claimant:",     claim.claimant_name || "—",
     "UW Ref.:",      uwRefValue],
  ];
  return (
    <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "100%" }}>
      <colgroup>
        <col style={{ width: "13%" }} />  {/* left labels */}
        <col style={{ width: "37%" }} />  {/* left values */}
        <col style={{ width: "11%" }} />  {/* right labels */}
        <col style={{ width: "39%" }} />  {/* right values */}
      </colgroup>
      <tbody>
        {rows.map(([lk, lv, rk, rv], i) => (
          <tr key={i}>
            <td style={metaKeyStyle}>{lk}</td>
            <td style={metaValStyle}>{lv}</td>
            <td style={metaKeyStyle}>{rk}</td>
            <td style={metaValStyle}>{rv}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ClaimMetaGrid({ claim }) {
  // WNW canonical vocabulary:
  //   "Claimant" = the injured person (was "Insured Name" on the legacy report)
  //   "Insured"  = the policy-holding company (was "Assured" on the legacy report)
  // UW Ref. enumerates every underwriter on the slip with share % and ref #.
  const uwList = claim.underwriters || [];
  const uwRefValue = uwList.length === 0 ? null : (
    <div>
      {uwList.map((uw, i) => (
        <div key={i} style={{ marginBottom: 2 }}>
          <span style={{ fontWeight: uw.is_lead ? 600 : 400 }}>
            {uw.company_name}
          </span>
          {uw.participation_pct != null && (
            <span style={{ color: "#777", marginLeft: 4 }}>
              ({uw.participation_pct.toFixed(1)}%)
            </span>
          )}
          {uw.reference_number && (
            <span style={{ color: "#444", marginLeft: 4 }}>
              — {uw.reference_number}
            </span>
          )}
          {uw.is_lead && (
            <span style={{ color: "#777", marginLeft: 4, fontStyle: "italic", fontSize: 9 }}>
              (Lead)
            </span>
          )}
        </div>
      ))}
    </div>
  );

  const left = [
    ["Claim Number:", claim.claim_number || ""],
    ["Date of Loss:", fmt.date(claim.date_of_loss)],
    ["Claimant:",     claim.claimant_name || ""],
  ];
  const right = [
    ["Vessel:",  claim.vessel_name || ""],
    ["Insured:", claim.insured_name || ""],
    ["UW Ref.",  uwRefValue],
  ];
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", margin: "10px 0 12px" }}>
      <tbody>
        {left.map(([k, v], i) => (
          <tr key={i}>
            <td style={metaKeyStyle}>{k}</td>
            <td style={metaValStyle}>{v || "—"}</td>
            <td style={metaKeyStyle}>{right[i][0]}</td>
            <td style={metaValStyle}>{right[i][1] || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
const metaKeyStyle = {
  fontSize: 10, fontWeight: 700, color: "#333",
  padding: "2px 8px 2px 0", verticalAlign: "top",
  whiteSpace: "nowrap",   // labels MUST NOT wrap (Claim Number stays one line)
};
const metaValStyle = {
  fontSize: 10, color: "#555",
  padding: "2px 12px 2px 0", verticalAlign: "top",
  wordBreak: "break-word",
};

function money(n) {
  if (n == null) return "—";
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ────────────────────────────────────────────────────────────────────
// TrustActivityZone — money INTO trust + running balance.
// Shows above the main payment table when the claim has any
// trust_contributions rows.  Sub-table: per-contributor totals with
// role badge + last deposit date.  Footer: total in / total out / net
// balance, with red treatment when the balance goes negative (over-spent).
// TRUST-BACKOFFICE-1 (2026-05-28).
// ────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────
// Party Settlement Ledger (PAYMENT-HISTORY-REDESIGN C3): the merged
// replacement for Trust Activity + Settlement. One row per party —
// Insured first-class, then slip subscribers, then other funders.
// Funded (cash in) and Owed (loss waterfall) are CO-EQUAL columns
// (Kuhrt decision); Net position = Funded − Owed reconciles them.
// "Share above deductible" labeled explicitly. Deposit detail expands
// per party. Trust cash In/Out/Balance stays as the reconciliation
// footer (it must equal the sum of net positions when funding is all
// trust-borne). Money columns: nowrap + tabular-nums + minWidth so
// seven-figure values never collide.
// ────────────────────────────────────────────────────────────────────
function PartyLedgerZone({ ledger, trustActivity, settlement, theme }) {
  const [openParty, setOpenParty] = useState(null);
  const lg = ledger || {};
  const rows = lg.rows || [];
  const ta = trustActivity || {};
  const detail = ta.contributions_detail || [];
  const sr = settlement || {};
  const ded = sr.deductible_status || {};
  const aad = sr.aad;
  const hasDed = Number(ded.deductible || 0) > 0;
  if (!rows.length) return null;
  const negBal = Number(ta.trust_balance || 0) < 0;
  const pct = (p) => (p == null ? "—" : Number(p).toFixed(1) + "%");
  const moneyCell = (v, opts = {}) => (
    <Td align="right" nowrap>
      <span style={{
        fontVariantNumeric: "tabular-nums",
        display: "inline-block", minWidth: 96,
        color: opts.signed && v != null && Number(v) < 0 ? "#dc2626"
             : opts.signed && v != null && Number(v) > 0 ? "#15803d" : "inherit",
        fontWeight: opts.bold ? 700 : 400,
      }}>{v == null ? "—" : money(v)}</span>
    </Td>
  );
  const depositsFor = (row) =>
    detail.filter(d => row.role === "insured"
      ? d.contributor_role === "insured"
      : d.contributor_name === row.party_key);
  const totals = rows.reduce((a, r) => ({
    funded: a.funded + Number(r.funded || 0),
    owed:   a.owed + Number(r.owed || 0),
    net:    a.net + Number(r.net_position || 0),
  }), { funded: 0, owed: 0, net: 0 });
  const unalloc = Object.entries(lg.unallocated || {});
  return (
    <div style={{ marginBottom: 14, padding: 12, border: "1px solid #e2e8f0", borderRadius: 6, background: "#f8fafc" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.primary, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Party Settlement Ledger
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
        <thead>
          <tr style={{ background: "#e2e8f0" }}>
            <Th align="left">Party</Th>
            <Th align="left">Role</Th>
            <Th align="right">Share above deductible</Th>
            <Th align="right">Funded (in)</Th>
            <Th align="right">Owed (waterfall)</Th>
            <Th align="right">Share of Reserve</Th>
            <Th align="right">Net Position</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const deps = depositsFor(r);
            const open = openParty === r.party_key;
            return (
              <Fragment key={i}>
                <tr style={{ borderBottom: "1px solid #e2e8f0",
                             background: r.role === "insured" ? theme.primary + "0d" : undefined }}>
                  <Td bold={r.role === "insured"}>
                    {r.party}{r.is_lead ? <span style={{ marginLeft: 6, fontSize: 9, color: theme.primary, fontWeight: 600 }}>(Lead)</span> : null}
                    {deps.length > 0 && (
                      <button onClick={() => setOpenParty(open ? null : r.party_key)}
                              style={{ marginLeft: 8, fontSize: 9, border: "1px solid #cbd5e1",
                                       borderRadius: 6, background: "white", cursor: "pointer",
                                       padding: "0 6px", color: "#475569" }}>
                        {open ? "▾ hide deposits" : `▸ ${deps.length} deposit${deps.length !== 1 ? "s" : ""}`}
                      </button>
                    )}
                  </Td>
                  <Td>{roleBadge(r.role)}</Td>
                  <Td align="right" nowrap>{pct(r.share_above_deductible_pct)}</Td>
                  {moneyCell(r.funded)}
                  {moneyCell(r.owed)}
                  {moneyCell(r.share_of_reserve)}
                  {moneyCell(r.net_position, { signed: true, bold: true })}
                </tr>
                {open && deps.map((d, j) => (
                  <tr key={`d${j}`} style={{ background: "#eef2f7", fontSize: 9.5 }}>
                    <Td colSpan={3}>
                      <span style={{ marginLeft: 18, color: "#64748b" }}>
                        {d.deposit_date ? new Date(d.deposit_date + "T00:00").toLocaleDateString("en-US") : "—"}
                        {d.description ? ` — ${d.description}` : ""}
                      </span>
                    </Td>
                    {moneyCell(d.amount)}
                    <Td colSpan={3}></Td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "2px solid " + theme.primary, fontWeight: 700 }}>
            <Td colSpan={3}>Total</Td>
            {moneyCell(totals.funded, { bold: true })}
            {moneyCell(totals.owed, { bold: true })}
            <Td></Td>
            {moneyCell(totals.net, { signed: true, bold: true })}
          </tr>
        </tfoot>
      </table>
      {lg.general_trust_note && (
        <div style={{ fontSize: 9.5, fontStyle: "italic", color: "#64748b", marginTop: 6 }}>
          ✦ This Insured funds via a general trust that spans claims — its program-level deposits
          are not itemized per claim; general-trust-funded payments attribute to the Insured.
        </div>
      )}
      {unalloc.length > 0 && (
        <div style={{ fontSize: 9.5, color: "#b45309", marginTop: 4 }}>
          Unallocated: {unalloc.map(([k, v]) => `${k.replace(/_/g, " ").replace(/^ +| +$/g, "")} ${money(v)}`).join(" · ")}
          {" "}— needs Accounting attribution.
        </div>
      )}
      {/* Cash reconciliation footer (the old Trust Activity trio) */}
      <table style={{ marginLeft: "auto", marginTop: 8, fontSize: 10.5, borderCollapse: "collapse" }}>
        <tbody>
          <tr><Td align="right" bold>Total Trust In:</Td>{moneyCell(ta.total_trust_in)}</tr>
          <tr><Td align="right" bold>Total Trust Out (payments funded='trust'):</Td>
            <Td align="right" nowrap>({money(ta.total_trust_out)})</Td></tr>
          <tr style={{ fontWeight: 700 }}>
            <Td align="right" bold>Trust Balance:</Td>
            <Td align="right" nowrap>
              <span style={{ fontVariantNumeric: "tabular-nums", color: negBal ? "#dc2626" : "#15803d" }}>
                {money(ta.trust_balance)}
              </span>
            </Td>
          </tr>
          {hasDed && (
            <>
              <tr><Td align="right" bold>Deductible Satisfied:</Td>{moneyCell(ded.satisfied)}</tr>
              <tr><Td align="right" bold>Deductible Remaining:</Td>{moneyCell(ded.remaining)}</tr>
            </>
          )}
          {aad && (
            <>
              <tr><Td align="right" bold>Aggregate Deductible (AAD):</Td>{moneyCell(aad.aggregate_amount)}</tr>
              {aad.apportionment_set && aad.apportioned_amount != null && (
                <tr><Td align="right" bold>This-Claim Apportionment:</Td>{moneyCell(aad.apportioned_amount)}</tr>
              )}
            </>
          )}
        </tbody>
      </table>
      {aad && !aad.apportionment_set && (
        <div style={{ textAlign: "right", fontSize: 9.5, fontStyle: "italic", color: "#64748b", marginTop: 4 }}>
          Aggregate-deductible apportionment for this claim is not yet set.
        </div>
      )}
    </div>
  );
}
function roleBadge(role) {
  const m = {
    insured:     {label: "Insured",     color: "#0e7490"},
    underwriter: {label: "Underwriter", color: "#2563eb"},
    assured:     {label: "Assured",     color: "#7c3aed"},
    owner:       {label: "Owner",       color: "#0891b2"},
    other:       {label: "Other",       color: "#64748b"},
  };
  const c = m[role] || {label: role || "—", color: "#64748b"};
  return (
    <span style={{
      display:"inline-block", padding:"1px 7px", borderRadius:8,
      backgroundColor: c.color + "22", color: c.color,
      fontSize: 9.5, fontWeight: 600, textTransform:"uppercase",
      letterSpacing: 0.3,
    }}>{c.label}</span>
  );
}

// (SettlementZone removed — PAYMENT-HISTORY-REDESIGN C3: the subscriber matrix
// merged into PartyLedgerZone above; deductible/AAD lines live in its footer;
// reimbursements-received surfaces in the NetDueBridge below.)

function PaymentTable({ rows, catKeys, catLabels, theme }) {
  // Column percentages sum to exactly 100% so the table fills the available
  // width.  Money columns + Date columns use white-space:nowrap (via Td's
  // nowrap prop) so currency values never wrap mid-figure.
  return (
    <table style={{
      width: "100%", borderCollapse: "collapse", fontSize: 9,
      border: `1px solid ${BORDER_GRAY}`, tableLayout: "fixed",
    }}>
      <colgroup>
        <col style={{ width: "9%" }} />      {/* Payee */}
        <col style={{ width: "6.5%" }} />    {/* Paid By/Rcvd From */}
        <col style={{ width: "9.5%" }} />    {/* Description */}
        <col style={{ width: "6%" }} />      {/* Check Date — wider so it doesn't collide w/ Check No. */}
        <col style={{ width: "5%" }} />      {/* Check No. */}
        <col style={{ width: "6.5%" }} />    {/* Date of Service */}
        {catKeys.map(k => <col key={k} style={{ width: "4.7%" }} />)}  {/* 10 cats × 4.7% = 47% */}
        <col style={{ width: "10.5%" }} />   {/* Total Exp & Fees — fits $9,999,999.99 */}
      </colgroup>
      <thead>
        <tr style={{ background: theme.headerBg, color: theme.headerText }}>
          <Th align="left">Payee</Th>
          <Th align="left">Paid By/<br/>Rcvd From</Th>
          <Th align="left">Description</Th>
          <Th align="center">Check<br/>Date</Th>
          <Th align="center">Check<br/>No.</Th>
          <Th align="center">Date of<br/>Service</Th>
          {catKeys.map(k => <Th key={k} align="right">{catLabels[k]}</Th>)}
          <Th align="right">Total Exp.<br/>&amp; Fees</Th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={6 + catKeys.length + 1}
                style={{ textAlign: "center", padding: 14, fontStyle: "italic", color: TEXT_MUTED }}>
              No payments recorded for this claim.
            </td>
          </tr>
        ) : rows.map((r, i) => (
          <tr key={r.id} style={{ background: i % 2 ? BG_ALT : "white" }}>
            <Td align="left">{r.payee || "—"}</Td>
            <Td align="left">{r.funding_label || "—"}</Td>
            <Td align="left">{r.description || ""}</Td>
            <Td align="center" nowrap>{fmt.date(r.check_date)}</Td>
            <Td align="center" nowrap>{r.check_number || ""}</Td>
            <Td align="center" nowrap>{fmt.date(r.date_of_service)}</Td>
            {catKeys.map(k => <Td key={k} align="right" nowrap>{money(r.categories[k])}</Td>)}
            <Td align="right" bold nowrap>{money(r.total_exp_fees)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CategoryTotalsRow({ catKeys, catTotals, grandTotal, theme }) {
  // Same column widths as the main PaymentTable so figures line up vertically.
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9, marginTop: 4,
                    tableLayout: "fixed",
                    borderTop: `2px solid ${theme.primary}`,
                    borderBottom: `1px solid ${theme.primary}` }}>
      <colgroup>
        <col style={{ width: "9%" }} />
        <col style={{ width: "6.5%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "5%" }} />
        <col style={{ width: "4.5%" }} />
        <col style={{ width: "7%" }} />
        {catKeys.map(k => <col key={k} style={{ width: "4.7%" }} />)}
        <col style={{ width: "11%" }} />
      </colgroup>
      <tbody>
        <tr style={{ fontWeight: 700 }}>
          <Td colSpan={5}></Td>
          <Td align="right" nowrap>Totals:</Td>
          {catKeys.map(k => <Td key={k} align="right" nowrap>{money(catTotals[k])}</Td>)}
          <Td align="right" nowrap>{money(grandTotal)}</Td>
        </tr>
      </tbody>
    </table>
  );
}

// ────────────────────────────────────────────────────────────────────
// Net-due bridge (PAYMENT-HISTORY-REDESIGN C4): replaces the legacy
// accounting block. The ONE identity (review F9):
//   net due to insured = insured funded − insured borne − reimbursements
// where "borne" is the waterfall retention (AAD-capped). Indemnity /
// Claim-Expense rollup (LD-ANALYTICS-003 via category_kind) leads the
// block; Net Due stays last (D5 section order).
// ────────────────────────────────────────────────────────────────────
function NetDueBridge({ ledger, kindRollup, settlement, summary, pendingIRItems, theme }) {
  const br = (ledger && ledger.bridge) || {};
  const kr = kindRollup || {};
  const sr = settlement || {};
  const rrTotal = Number(br.reimbursements_received != null ? br.reimbursements_received
                                                            : sr.reimbursements_received_total || 0);
  const netDue = br.net_due_to_insured != null ? br.net_due_to_insured
                                               : (summary || {}).net_due_to_owner;
  const pendingTotal = Number((summary || {}).pending_ir_total || 0);
  const netLessPending = netDue != null ? Number(netDue) - pendingTotal : null;
  const hasKinds = ["indemnity", "expense", "offset", "uncategorized"]
    .some(k => Number(kr[k] || 0) !== 0);

  const lines = [];
  if (hasKinds) {
    lines.push(["Indemnity", kr.indemnity]);
    lines.push(["Claim Expense", kr.expense]);
    if (Number(kr.offset || 0) !== 0)        lines.push(["Recoveries / Offsets", kr.offset]);
    if (Number(kr.uncategorized || 0) !== 0) lines.push(["Uncategorized", kr.uncategorized]);
    lines.push("__spacer__");
  }
  lines.push(["Total Paid on Claim",              br.total_paid]);
  lines.push(["Insured's Borne Cost (waterfall" + (br.aad_capped ? ", AAD-capped" : "") + ")", br.insured_borne]);
  lines.push(["Insured Funded (deposits + direct)", br.insured_funded]);
  if (rrTotal > 0) lines.push(["Reimbursements Received (UW → Insured)", rrTotal]);
  lines.push("__spacer__");
  lines.push(["Net Due to Insured", netDue, true]);
  (pendingIRItems || []).forEach(ir => {
    const label = `Pending IR ${ir.ir_number != null ? "#" + ir.ir_number : ""}${ir.label ? ", " + ir.label : (ir.ir_date ? ", " + ir.ir_date : "")}`;
    lines.push([label, ir.amount]);
  });
  if (pendingTotal && (pendingIRItems || []).length === 0) {
    lines.push(["Pending IR Total", pendingTotal]);
  }
  lines.push(["Net Due to Insured, Less Pending", netLessPending, true]);

  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
      <table style={{ borderCollapse: "collapse", fontSize: 10 }}>
        <tbody>
          {lines.map((row, i) => {
            if (row === "__spacer__") {
              return <tr key={i}><td style={{ height: 6 }} colSpan={2}></td></tr>;
            }
            const [label, value, emphasize] = row;
            return (
              <tr key={i}>
                <td style={{ textAlign: "right", fontWeight: 700, color: "#333",
                             padding: "2px 12px 2px 0",
                             whiteSpace: "nowrap",
                             fontSize: emphasize ? 11 : 10 }}>{label}:</td>
                <td style={{ textAlign: "right",
                             color: emphasize ? theme.primary : "#1A1A1A",
                             padding: "2px 0",
                             minWidth: 130,        /* fits "$9,999,999.99" */
                             whiteSpace: "nowrap",
                             fontVariantNumeric: "tabular-nums",
                             fontWeight: emphasize ? 700 : 400,
                             fontSize: emphasize ? 11 : 10 }}>
                  {money(value)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ConfidentialityLine({ branding }) {
  const text = (branding && branding.confidentiality_footer) ||
    "Confidential — HIPAA-protected information. Distribute only as authorized.";
  return (
    <div style={{
      marginTop: 18, paddingTop: 8,
      fontSize: 9, fontStyle: "italic", color: TEXT_MUTED,
      textAlign: "center",
    }}>
      {text}
    </div>
  );
}

function PageFooter({ branding, theme }) {
  const b = branding || {};
  const left = [];
  if (b.phone)             left.push(`Office: ${b.phone}`);
  if (b.fax)               left.push(`Fax: ${b.fax}`);
  if (b.website_corporate) left.push(b.website_corporate);
  const right = [];
  if (b.address_line1)  right.push(b.address_line1);
  if (b.address_line2)  right.push(b.address_line2);
  if (b.city_state_zip) right.push(b.city_state_zip);
  if (b.email)          right.push(b.email);
  if (left.length === 0 && right.length === 0) return null;
  const n = Math.max(left.length, right.length);
  return (
    <div style={{ marginTop: 28, borderTop: `1px solid ${theme.footerAccent}`, paddingTop: 6 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 8.5, color: "#555" }}>
        <tbody>
          {Array.from({ length: n }).map((_, i) => (
            <tr key={i}>
              <td style={{ textAlign: "left",  padding: "1px 0", lineHeight: 1.5 }}>{left[i] || ""}</td>
              <td style={{ textAlign: "right", padding: "1px 0", lineHeight: 1.5 }}>{right[i] || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ align, children }) {
  // Cleaner header cell: tighter line-height + slight letter-spacing for
  // the small caps look at 8.5pt.
  return (
    <th style={{
      textAlign: align, padding: "6px 4px", fontWeight: 700, fontSize: 8.5,
      lineHeight: 1.15, letterSpacing: 0.2,
      borderBottom: `1px solid rgba(255,255,255,0.4)`,
      verticalAlign: "middle",
    }}>{children}</th>
  );
}

function Td({ align, children, colSpan, bold, nowrap }) {
  return (
    <td colSpan={colSpan}
        style={{
          textAlign: align || "left",
          padding: "3px 4px",
          borderBottom: `1px solid ${BORDER_GRAY}`,
          fontWeight: bold ? 700 : 400,
          wordBreak: nowrap ? "normal" : "break-word",
          whiteSpace: nowrap ? "nowrap" : "normal",
          // Tabular numerals keep right-aligned money columns visually
          // stable across rows (every digit slot is equal-width).
          fontVariantNumeric: nowrap ? "tabular-nums" : "normal",
          verticalAlign: "top",
        }}>
      {children}
    </td>
  );
}

function DisabledMenuItem({ label, hint }) {
  return (
    <div title={hint} role="menuitem" aria-disabled="true"
         style={{ padding: "8px 12px", color: "#888", cursor: "not-allowed",
                  fontSize: 13, borderRadius: 3, userSelect: "none" }}>
      <div>{label}</div>
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 2, fontStyle: "italic" }}>{hint}</div>
    </div>
  );
}
